import React, { Component } from "react";
import LocalSelectTable from "../../Tables/LocalSelectTable";
import { LabeledDropDownSelect } from "../../DropDownSelect";
import MaxHeightDropDownSelect from "../../InputControls/MaxHeightDropDownSelect";
import { ButtonWithIcon } from "../../ButtonWithIcon";
import { iconMap, clamp } from "../../../utilities/utilities";
import CustomPagination from "../../Tables/CustomPagination";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";

import "./MandatoryProfilingDialog.css";

// Component to display selected items count and bulk apply controls
class BulkApplySection extends Component {
	constructor(props) {
		super(props);
		this.state = {
			documentType: null,
			documentStatus: null,
			documentTags: [],
		};
	}

	handleFieldChange = (fieldName, value) => {
		this.setState({ [fieldName]: value });
	};

	handleCreateOption = (fieldName, inputValue) => {
		const newOption = this.props.onCreateOption(fieldName, inputValue);

		// For multi-select tags, add to existing values
		if (fieldName === "documentTags") {
			const currentValues = this.state.documentTags || [];
			this.setState({
				documentTags: [...currentValues, newOption],
			});
		} else {
			// For single-select fields, just set the value
			this.setState({ [fieldName]: newOption });
		}
	};

	handleApplyToSelected = () => {
		const { documentType, documentStatus, documentTags } = this.state;

		// Apply bulk metadata to all selected items
		this.props.onApplyBulkMetadata({
			documentType,
			documentStatus,
			documentTags,
		});
	};

	isApplyButtonDisabled = () => {
		const { documentType, documentStatus, documentTags } = this.state;
		const { selectedItems } = this.props;

		const noItemsSelected = Object.keys(selectedItems).length === 0;
		const noMetadata =
			!documentType &&
			!documentStatus &&
			(!documentTags || documentTags.length === 0);

		return noItemsSelected || noMetadata;
	};

	render() {
		const {
			selectedItems,
			documentTypeOptions,
			documentStatusOptions,
			documentTagOptions,
		} = this.props;

		const count = Object.keys(selectedItems).length;

		return (
			<div>
				<div className="bulk-apply-section">
					<div
						style={{
							display: "flex",
							gap: "12px",
							alignItems: "flex-end",
						}}
					>
						<div
							style={{
								flex: 1,
								position: "relative",
								zIndex: 10000,
							}}
						>
							<LabeledDropDownSelect
								label="Type"
								name="documentType"
								placeholder="Type"
								isCreatable={
									this.props.userRights
										.CRUD_DOCUMENT_TYPES === true
								}
								isClearable
								isAsync
								options={documentTypeOptions}
								value={this.state.documentType}
								handler={this.handleFieldChange}
								onCreateOption={(inputValue) =>
									this.handleCreateOption(
										"documentType",
										inputValue
									)
								}
							/>
						</div>
						<div
							style={{
								flex: 1,
								position: "relative",
								zIndex: 10000,
							}}
						>
							<LabeledDropDownSelect
								label="Status"
								name="documentStatus"
								placeholder="Status"
								isCreatable={
									this.props.userRights
										.CRUD_DOCUMENT_STATUSES === true
								}
								isClearable
								isAsync
								options={documentStatusOptions}
								value={this.state.documentStatus}
								handler={this.handleFieldChange}
								onCreateOption={(inputValue) =>
									this.handleCreateOption(
										"documentStatus",
										inputValue
									)
								}
							/>
						</div>
						<div style={{ flex: 1, position: "relative" }}>
							<div
								style={{
									position: "absolute",
									top: -59,
									width: "100%",
								}}
							>
								<MaxHeightDropDownSelect
									label="Tags"
									isMulti
									name="documentTags"
									placeholder="Tags"
									isCreatable={
										this.props.userRights
											.CRUD_DOCUMENT_TAGS === true
									}
									isAsync
									options={documentTagOptions}
									value={this.state.documentTags}
									handler={this.handleFieldChange}
									onCreateOption={(inputValue) =>
										this.handleCreateOption(
											"documentTags",
											inputValue
										)
									}
								/>
							</div>
						</div>
						<div style={{ minWidth: 130, paddingBottom: 2 }}>
							<ButtonWithIcon
								handler={this.handleApplyToSelected}
								disabled={this.isApplyButtonDisabled()}
								buttonText={`Apply to ${count}`}
								iconName="check"
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

/*
	props looks like this:


      {
    "promptForProfiling": true,
    "files": [
        {
            "filename": "Amity Island Maritime Law Memorandum.docx",
            "requiredProfiling": [
                "documentType",
                "documentTags"
            ],
            "promptToProfile": true
        },
        {
            "filename": "Andy Dufrense Wrongful Imprisonment Lawsuit.docx",
            "requiredProfiling": [
                "documentType",
                "documentTags"
            ],
            "promptToProfile": true
        },
        {
            "filename": "Shelley v. Kraemer.pdf",
            "requiredProfiling": null,
            "promptToProfile": false
        },
        {
            "filename": "Nuclear_symbol.svg",
            "requiredProfiling": null,
            "promptToProfile": false
        },
        {
            "filename": "Book1.xlsx",
            "requiredProfiling": [
                "documentType",
                "documentTags"
            ],
            "promptToProfile": true
        }
    ]
}

*/

class MandatoryProfilingDialog extends Component {
	constructor(props) {
		super(props);

		// Initialize state with empty values for each file
		const { files = [], availableOptions } = props;
		const filesToProfile = files.filter((file) => file.promptToProfile);

		const initialProfilingData = {};
		filesToProfile.forEach((file, index) => {
			initialProfilingData[index] = {
				documentType: null,
				documentStatus: null,
				documentTags: [],
			};
		});

		const transformToDropdownFormat = (names) =>
			names ? names.map((name) => ({ value: name, label: name })) : [];

		this.state = {
			profilingData: initialProfilingData,
			documentTypeOptions: transformToDropdownFormat(
				availableOptions?.types
			),
			documentStatusOptions: transformToDropdownFormat(
				availableOptions?.statuses
			),
			documentTagOptions: transformToDropdownFormat(
				availableOptions?.tags
			),
			selectedItems: {},
			headerChecked: false,
		};

		// Tab filter map for All/Processed/Un-Processed tabs
		this.filterMap = {
			"Un-Processed": (row) => !this.isFileProcessed(row.id),
			Processed: (row) => this.isFileProcessed(row.id),
			All: () => true,
		};

		// Define columns once to prevent Cell function recreation
		// Determine which fields are required by ANY file
		const isTypeRequiredForAny = filesToProfile.some((file) =>
			(file.requiredProfiling || []).includes("documentType")
		);
		const isStatusRequiredForAny = filesToProfile.some((file) =>
			(file.requiredProfiling || []).includes("documentStatus")
		);
		const isTagsRequiredForAny = filesToProfile.some((file) =>
			(file.requiredProfiling || []).includes("documentTags")
		);

		const isFieldRequired = (row, fieldName) => {
			return row.requiredProfiling.includes(fieldName);
		};

		this.columns = [
			{
				Header: "Filename",
				accessor: "filename",
				unhidable: true,
				Cell: (row) => {
					const { iconComponent } = iconMap(row);
					const hasFolder =
						row.original.folder && row.original.folder !== "";
					return (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								minWidth: 0,
								height: "100%",
								justifyContent: hasFolder
									? "flex-start"
									: "center",
								alignItems: "flex-start",
								maxHeight: 38,
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									width: "stretch",
									minWidth: 0,
								}}
							>
								{iconComponent}
								<span
									style={{
										fontSize: "14px",
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
										minWidth: 0,
									}}
									title={row.original.filename}
								>
									{row.original.filename}
								</span>
							</div>
							{hasFolder ? (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										width: "stretch",
										minWidth: 0,
										marginTop: "2px",
									}}
								>
									<span
										style={{
											fontSize: "12px",
											color: "#666",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											minWidth: 0,
										}}
										title={row.original.folder}
									>
										{row.original.folder}
									</span>
								</div>
							) : null}
						</div>
					);
				},
			},
			{
				Header: `Type${isTypeRequiredForAny ? " *" : ""}`,
				accessor: "documentType",
				unhidable: true,
				sortable: false,
				filterable: false,
				Cell: (row) => {
					const index = row.original.id;
					return (
						<div style={{ minWidth: 0 }}>
							<LabeledDropDownSelect
								name={`documentType_${index}`}
								placeholder="Type"
								isCreatable={
									this.props.userRights
										.CRUD_DOCUMENT_TYPES === true
								}
								isClearable
								isAsync
								options={this.state.documentTypeOptions}
								value={
									this.state.profilingData[index]
										?.documentType
								}
								handler={(name, value) =>
									this.handleFieldChange(
										index,
										"documentType",
										value
									)
								}
								onCreateOption={(inputValue) => {
									const newOption =
										this.handleCreateAndAddOption(
											"documentType",
											inputValue
										);
									this.handleFieldChange(
										index,
										"documentType",
										newOption
									);
								}}
								className={
									isFieldRequired(
										row.original,
										"documentType"
									)
										? "required-field"
										: ""
								}
							/>
						</div>
					);
				},
			},
			{
				Header: `Status${isStatusRequiredForAny ? " *" : ""}`,
				accessor: "documentStatus",
				unhidable: true,
				sortable: false,
				filterable: false,
				Cell: (row) => {
					const index = row.original.id;
					return (
						<div style={{ minWidth: 0 }}>
							<LabeledDropDownSelect
								name={`documentStatus_${index}`}
								placeholder="Status"
								isCreatable={
									this.props.userRights
										.CRUD_DOCUMENT_STATUSES === true
								}
								isClearable
								isAsync
								options={this.state.documentStatusOptions}
								value={
									this.state.profilingData[index]
										?.documentStatus
								}
								handler={(name, value) =>
									this.handleFieldChange(
										index,
										"documentStatus",
										value
									)
								}
								onCreateOption={(inputValue) => {
									const newOption =
										this.handleCreateAndAddOption(
											"documentStatus",
											inputValue
										);
									this.handleFieldChange(
										index,
										"documentStatus",
										newOption
									);
								}}
								className={
									isFieldRequired(
										row.original,
										"documentStatus"
									)
										? "required-field"
										: ""
								}
							/>
						</div>
					);
				},
			},
			{
				Header: `Tags${isTagsRequiredForAny ? " *" : ""}`,
				accessor: "documentTags",
				unhidable: true,
				sortable: false,
				filterable: false,
				Cell: (row) => {
					const index = row.original.id;
					return (
						<div style={{ minWidth: 0 }}>
							<MaxHeightDropDownSelect
								isMulti
								name={`documentTags_${index}`}
								placeholder="Tags"
								isCreatable={
									this.props.userRights.CRUD_DOCUMENT_TAGS ===
									true
								}
								isAsync
								options={this.state.documentTagOptions}
								value={
									this.state.profilingData[index]
										?.documentTags
								}
								handler={(name, value) =>
									this.handleFieldChange(
										index,
										"documentTags",
										value
									)
								}
								onCreateOption={(inputValue) => {
									const newOption =
										this.handleCreateAndAddOption(
											"documentTags",
											inputValue
										);
									// For multi-select, add to existing values
									const currentValues =
										this.state.profilingData[index]
											?.documentTags || [];
									this.handleFieldChange(
										index,
										"documentTags",
										[...currentValues, newOption]
									);
								}}
								className={
									isFieldRequired(
										row.original,
										"documentTags"
									)
										? "required-field"
										: ""
								}
							/>
						</div>
					);
				},
			},
		];
	}

	componentDidMount() {
		// Trigger initial validation
		if (this.props.onValidationChange) {
			this.props.onValidationChange(this.isAllRequiredFieldsFilled());
		}
	}

	isFileProcessed = (fileIndex) => {
		const { files = [] } = this.props;
		const filesToProfile = files.filter((file) => file.promptToProfile);
		const file = filesToProfile[fileIndex];

		if (!file) return false;

		const fileData = this.state.profilingData[fileIndex];
		if (!fileData) return false;

		const requiredFields = file.requiredProfiling || [];

		return requiredFields.every((field) => {
			const value = fileData[field];

			if (field === "documentTags") {
				return Array.isArray(value) && value.length > 0;
			}
			return (
				value &&
				(typeof value === "string" ||
					(value.value && value.value.trim()))
			);
		});
	};

	handleFieldChange = (fileIndex, fieldName, value) => {
		this.setState(
			(prevState) => ({
				profilingData: {
					...prevState.profilingData,
					[fileIndex]: {
						...prevState.profilingData[fileIndex],
						[fieldName]: value,
					},
				},
			}),
			() => {
				// Call parent's onChange if provided
				if (this.props.onChange) {
					this.props.onChange(this.state.profilingData);
				}
				// Call validation callback if provided
				if (this.props.onValidationChange) {
					this.props.onValidationChange(
						this.isAllRequiredFieldsFilled()
					);
				}
			}
		);
	};

	handleBulkMetadataApply = (bulkMetadata) => {
		const { documentType, documentStatus, documentTags } = bulkMetadata;
		const selectedIndices = Object.keys(this.state.selectedItems);

		// Batch all updates into a single setState
		this.setState(
			(prevState) => {
				const newProfilingData = { ...prevState.profilingData };

				// Apply each non-null field to all selected items
				selectedIndices.forEach((index) => {
					const fileIndex = parseInt(index);

					newProfilingData[fileIndex] = {
						...newProfilingData[fileIndex],
					};

					if (documentType) {
						newProfilingData[fileIndex].documentType = documentType;
					}
					if (documentStatus) {
						newProfilingData[fileIndex].documentStatus =
							documentStatus;
					}
					if (documentTags && documentTags.length > 0) {
						newProfilingData[fileIndex].documentTags = documentTags;
					}
				});

				return {
					profilingData: newProfilingData,
					selectedItems: {},
					headerChecked: false,
				};
			},
			() => {
				// Call parent's onChange if provided
				if (this.props.onChange) {
					this.props.onChange(this.state.profilingData);
				}
				// Call validation callback if provided
				if (this.props.onValidationChange) {
					this.props.onValidationChange(
						this.isAllRequiredFieldsFilled()
					);
				}
			}
		);
	};

	isAllRequiredFieldsFilled = () => {
		const { files = [] } = this.props;
		const filesToProfile = files.filter((file) => file.promptToProfile);

		const isValid = filesToProfile.every((file, index) => {
			const fileData = this.state.profilingData[index];
			if (!fileData) return false;

			const requiredFields = file.requiredProfiling || [];

			return requiredFields.every((field) => {
				const value = fileData[field];

				// Check if field has a value
				if (field === "documentTags") {
					const valid = Array.isArray(value) && value.length > 0;
					return valid;
				}
				// For other fields, check if they have a truthy value and not just empty objects
				const valid =
					value &&
					(typeof value === "string" ||
						(value.value && value.value.trim()));
				return valid;
			});
		});

		return isValid;
	};

	// Map field names to their options array keys
	fieldToOptionsKeyMap = {
		documentType: "documentTypeOptions",
		documentStatus: "documentStatusOptions",
		documentTags: "documentTagOptions",
	};

	handleCreateAndAddOption = (fieldName, inputValue) => {
		// Create new option
		const newOption = {
			id: Date.now(),
			label: inputValue,
			value: inputValue,
		};

		// Get the options array key from the map
		const optionsKey = this.fieldToOptionsKeyMap[fieldName];

		// Add to shared options array
		this.setState((prevState) => ({
			[optionsKey]: [...prevState[optionsKey], newOption],
		}));

		return newOption;
	};

	getProfilingData = () => {
		const { files = [] } = this.props;
		const filesToProfile = files.filter((file) => file.promptToProfile);

		return filesToProfile.map((file, index) => ({
			filename: file.filename,
			...this.state.profilingData[index],
		}));
	};

	handleCheckboxChange = (id, checked) => {
		this.setState((prevState) => {
			const newSelectedItems = { ...prevState.selectedItems };
			if (checked) {
				newSelectedItems[id] = true;
			} else {
				delete newSelectedItems[id];
			}

			// Update header checkbox state
			const allSelected =
				Object.keys(newSelectedItems).length ===
				this.getTableData().length;

			return {
				selectedItems: newSelectedItems,
				headerChecked: allSelected,
			};
		});
	};

	handleHeaderCheckboxChange = (visibleData) => {
		this.setState((prevState) => {
			const newChecked = !prevState.headerChecked;
			const newSelectedItems = {};

			if (newChecked) {
				// Select only visible/filtered items
				visibleData.forEach((row) => {
					newSelectedItems[row.id] = true;
				});
			}
			// else deselect all (leave empty object)

			return {
				selectedItems: newSelectedItems,
				headerChecked: newChecked,
			};
		});
	};

	getTableData = () => {
		const { files = [] } = this.props;
		const filesToProfile = files.filter((file) => file.promptToProfile);

		return filesToProfile.map((file, index) => ({
			id: index,
			filename: file.filename,
			type: "File",
			requiredProfiling: file.requiredProfiling || [],
			folder: file.folder ? "/" + file.folder : "",
		}));
	};

	getCustomTabNames = () => {
		const { files = [] } = this.props;
		const filesToProfile = files.filter((file) => file.promptToProfile);
		const total = filesToProfile.length;

		// Count how many files are processed
		let processedCount = 0;
		filesToProfile.forEach((file, index) => {
			if (this.isFileProcessed(index)) {
				processedCount++;
			}
		});

		const unprocessedCount = total - processedCount;

		return {
			All: `All (${total})`,
			Processed: `Profiled (${processedCount}/${total})`,
			"Un-Processed": `Needs Profiling (${unprocessedCount}/${total})`,
		};
	};

	getResponsivePageSize = () => {
		// show a number of rows per page that will fit within the full screen modal
		const screenHeight = this.props.screenSize?.h || window.innerHeight;

		// Base offset for dialog chrome (header, tabs, pagination, margins)
		const baseOffset = 320;

		// Add bulk section height when expanded (~90px measured)
		const bulkSectionHeight = this.props
			.mandatoryProfilingDialogBulkProcessingExpanded
			? 90
			: 0;

		// Each row is about 60px high
		const totalOffset = baseOffset + bulkSectionHeight;
		const availableSpace = screenHeight * 0.9 - totalOffset;
		return clamp(Math.floor(availableSpace / 60), 2, 10);
	};

	render() {
		const { files = [] } = this.props;

		// Filter files that need profiling
		const filesToProfile = files.filter((file) => file.promptToProfile);

		if (filesToProfile.length === 0) {
			return (
				<div className="mandatory-profiling-dialog">
					<p>No files require profiling.</p>
				</div>
			);
		}

		const pageSize = this.getResponsivePageSize();

		return (
			<div className="mandatory-profiling-dialog">
				<div style={{ marginBottom: "10px" }}>
					<span
						style={{
							color: "var(--lxw-hyperlink)",
							cursor: "pointer",
							display: "inline-flex",
							alignItems: "center",
							fontSize: "14px",
							whiteSpace: "nowrap",
						}}
						onClick={
							this.props
								.toggleMandatoryProfilingDialogBulkProcessing
						}
					>
						{this.props
							.mandatoryProfilingDialogBulkProcessingExpanded ? (
							<>
								Hide Bulk Processing <IconChevronUp size={20} />
							</>
						) : (
							<>
								Show Bulk Processing{" "}
								<IconChevronDown size={20} />
							</>
						)}
					</span>
				</div>
				{this.props.mandatoryProfilingDialogBulkProcessingExpanded ? (
					<div className="bulk-apply-section">
						<BulkApplySection
							selectedItems={this.state.selectedItems}
							documentTypeOptions={this.state.documentTypeOptions}
							documentStatusOptions={
								this.state.documentStatusOptions
							}
							documentTagOptions={this.state.documentTagOptions}
							onApplyBulkMetadata={this.handleBulkMetadataApply}
							onCreateOption={this.handleCreateAndAddOption}
							userRights={this.props.userRights}
						/>
					</div>
				) : null}
				<div
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<LocalSelectTable
						tableUniqueID="mandatoryProfilingTable"
						data={this.getTableData()}
						columns={this.columns}
						selectedItems={this.state.selectedItems}
						headerChecked={this.state.headerChecked}
						onCheckboxChange={this.handleCheckboxChange}
						onHeaderCheckboxChange={this.handleHeaderCheckboxChange}
						hideColumnsButton={true}
						defaultPageSize={pageSize}
						minRows={pageSize}
						showPagination={true}
						uncontrolledPagination
						PaginationComponent={CustomPagination}
						tableStyle={{
							height: "100%",
							flexGrow: 1,
						}}
						tabsFilterMap={this.filterMap}
						defaultTabFilterValue="Un-Processed"
						/*
							CustomizableTable stores the current "tab" in the persistent storage,
							but for the profiling dialog we don't want the user to start on the "Processed"
							tab because it will always be empty.  If they left the table on "Processed",
							the next time they open the dialog it will go to the default tab
						*/
						excludeTabsFromPersistence={["Processed"]}
						customTabNames={this.getCustomTabNames()}
						noResultsText="No Documents"
					/>
				</div>
			</div>
		);
	}
}

export default MandatoryProfilingDialog;
